import { ace_index } from '@axo/shared/data-access/types';
import { AnchorButton, Button, Switch, Tooltip } from '@blueprintjs/core';
import { Dispatch, SetStateAction, useState } from 'react';
import { useLockAndOpenApplication } from '../../hooks/utils/useLockAndOpenApplication';
import { AceTable } from '../AceTable';
import { AceTag } from '../AceTag';
import { IconMenu } from '../IconMenu';
import { SearchPaginate } from '../SearchPaginate';
import { ColumnsDialog } from './ColumnsDialog';
import styles from './SearchResults.module.css';
import { ace_index_hooks } from '@axo/shared/data-access/hooks';
import { COLUMN_IDS, ColumnID, column } from './config';
import { ITableConfig } from '../../models/tableConfig';
import { SortIcon } from './SortIcon';
import { setPageNumber, togglePortfolio } from './state';

// SearchResults should take a ACE index query and present the query result
export function SearchResults({
  query,
  setQuery,
  columns,
  availableColumns = COLUMN_IDS,
  setColumns,
  sortable = false,
  pageSize = 20,
}: {
  query: ace_index.FindInput;
  setQuery: Dispatch<SetStateAction<ace_index.FindInput>>;
  columns: ColumnID[];
  availableColumns?: ColumnID[];
  setColumns?: Dispatch<SetStateAction<ColumnID[]>>;
  sortable?: boolean;
  pageSize?: number;
}) {
  const open = useLockAndOpenApplication(
    query.LoanApplicationMarketCountry ?? ''
  );

  const limitedQuery: ace_index.FindInput = {
    ...query,
    PaginationParameters: {
      ...(query.PaginationParameters ?? { PageNumber: 1 }),
      PageSize: pageSize,
    },
  };

  const results = ace_index_hooks.useACEIndexQuery(limitedQuery);

  const first =
    results.data !== undefined && results.data?.Parameters.PageNumber > 1
      ? () => setQuery(setPageNumber(1))
      : undefined;

  const prev =
    results.data !== undefined && results.data?.Parameters.PageNumber > 1
      ? () => setQuery(setPageNumber(results.data.Parameters.PageNumber - 1))
      : undefined;

  const next =
    results.data !== undefined &&
    results.data?.Parameters.PageNumber < results.data?.Parameters.TotalPages
      ? () => setQuery(setPageNumber(results.data.Parameters.PageNumber + 1))
      : undefined;

  const last =
    results.data !== undefined &&
    results.data?.Parameters.PageNumber < results.data?.Parameters.TotalPages
      ? () => setQuery(setPageNumber(results.data.Parameters.TotalPages))
      : undefined;

  function openAllApplications() {
    if (results.data) {
      const openPromises = results.data.Items.map(({ ID, HumanReadableID }) =>
        open({
          LoanApplicationID: ID,
          HumanReadableID,
        })
      );
      Promise.all(openPromises)
        // Since ACE-index is updated asynchroniously, we have no guarantee
        // that the index will be updated when the refetch is done.
        // This can cause the list not correctly showing which applications are locked.
        .then(() => results.refetch());
    }
  }

  const MAX_APPLICATIONS_TO_OPEN = 30;
  const openAllApplicationsDisabled =
    results.data && results.data.Items.length > MAX_APPLICATIONS_TO_OPEN;

  const [columnsOpen, setColumnsOpen] = useState(false);

  return (
    <div className={styles.container}>
      <IconMenu>
        {columns && setColumns && (
          <>
            <ColumnsDialog
              available={availableColumns}
              columns={columns}
              isOpen={columnsOpen}
              onClose={() => setColumnsOpen(false)}
              setColumns={(columns) => setColumns(columns)}
            />

            <Tooltip content="Show/hide and reorder columns">
              <Button
                minimal
                icon="eye-open"
                text="Columns"
                onClick={() => setColumnsOpen(true)}
              />
            </Tooltip>
          </>
        )}

        <Switch
          className={styles.control}
          style={{ marginBottom: 0 }}
          checked={query.AssignmentClientID === 'self'}
          onChange={() => setQuery(togglePortfolio())}
        >
          Show portfolio only
        </Switch>

        <Switch
          className={styles.control}
          style={{ marginBottom: 0 }}
          checked={query.LockLockedByID === ''}
          onChange={() => setQuery(toggleLockedApplications())}
        >
          Hide locked applications
        </Switch>

        {results.data ? (
          <AceTag classes={{ tag: styles.rose }}>
            {`${
              results.data.Parameters.TotalRows < 1001
                ? results.data.Parameters.TotalRows
                : '1000+'
            } results`}
          </AceTag>
        ) : null}

        <Tooltip
          placement="top"
          content={
            openAllApplicationsDisabled
              ? `Cannot open more than ${MAX_APPLICATIONS_TO_OPEN} results`
              : 'Open all applications in results list'
          }
        >
          <AnchorButton
            disabled={
              results.isIdle ||
              results.isFetching ||
              openAllApplicationsDisabled
            }
            icon="multi-select"
            minimal
            onClick={openAllApplications}
          />
        </Tooltip>

        <Tooltip content="Refresh results">
          <Button
            icon="refresh"
            loading={results.isRefetching}
            disabled={results.isIdle}
            minimal
            onClick={() => results.refetch()}
          />
        </Tooltip>
      </IconMenu>

      <AceTable<ace_index.LoanApplication>
        config={aceTableConfig(columns, sortable, query, setQuery)}
        data={results.data?.Items ?? []}
        error={
          results.error instanceof Error ? results.error.message : undefined
        }
        uniqueRowIDKey="ID"
        loading={results.isLoading}
      />

      <SearchPaginate
        config={{
          query: results,
          firstPage: first ?? undefined,
          lastPage: last ?? undefined,
          nextPage: next ?? undefined,
          previousPage: prev ?? undefined,
          disabledNext: !next,
          disabledPrevious: !prev,
        }}
      />
    </div>
  );
}

function aceTableConfig(
  columns: ColumnID[],
  sortable: boolean,
  query: ace_index.FindInput,
  setQuery: Dispatch<SetStateAction<ace_index.FindInput>>
): ITableConfig<ace_index.LoanApplication> {
  return {
    columns: {}, // Ignored, using only the "extra"
    extra: columns
      .map((columnID) => {
        const conf = column(columnID);

        if (!conf) {
          return undefined;
        }

        return {
          key: columnID,
          label: conf.label,
          render: (app: ace_index.LoanApplication) => conf.render(app),

          // TODO: this can be ripped out with the new sorting changes
          labelIcon:
            sortable && conf.sort ? (
              <SortIcon
                config={conf.sort}
                current={query.SortV2 ?? []}
                setCurrent={(sort) => setQuery(setSorting(sort))}
              />
            ) : undefined,
        };
      })
      .filter(notEmpty),
  };
}

function notEmpty<T>(value: T | undefined): value is T {
  return value !== undefined;
}

function setSorting(
  sort: ace_index.FindSort[]
): SetStateAction<ace_index.FindInput> {
  return (query) => ({
    ...setPageNumber(1)(query),
    SortV2: sort,
  });
}

function toggleLockedApplications(): SetStateAction<ace_index.FindInput> {
  return (query) => ({
    ...setPageNumber(1)(query),
    LockLockedByID: query.LockLockedByID === undefined ? '' : undefined,
  });
}

